import {
  selectAudioTrackByPeerID,
  selectPermissions,
  selectScreenShareAudioByPeerID,
  selectScreenShareByPeerID,
  selectVideoTrackByPeerID,
  useCustomEvent,
  useHMSActions,
  useHMSStore,
  useRemoteAVToggle,
} from "@100mslive/react-sdk";
import {
  Ellipsis,
  Mic,
  MicOff,
  MonitorX,
  UserX,
  Video,
  VideoOff,
  Volume2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Slider } from "@/components/ui/slider";

export const STOP_SCREENSHARE_EVENT = "REMOTE_STOP_SCREENSHARE";

/**
 * Actions on someone else's tile. Everyone can change how loud a person is
 * for them; muting, asking to unmute and removing depend on the 100ms role's
 * permissions (only the interviewer has them).
 */
export const TileMenu = ({ peer, isScreenShare = false }) => {
  const hmsActions = useHMSActions();
  const permissions = useHMSStore(selectPermissions);
  const audioTrack = useHMSStore(
    isScreenShare
      ? selectScreenShareAudioByPeerID(peer.id)
      : selectAudioTrackByPeerID(peer.id)
  );
  const videoTrack = useHMSStore(
    isScreenShare
      ? selectScreenShareByPeerID(peer.id)
      : selectVideoTrackByPeerID(peer.id)
  );
  const {
    isAudioEnabled,
    isVideoEnabled,
    volume,
    toggleAudio,
    toggleVideo,
    setVolume,
  } = useRemoteAVToggle(audioTrack?.id, videoTrack?.id);
  const { sendEvent } = useCustomEvent({ type: STOP_SCREENSHARE_EVENT });

  const canRemove = permissions?.removeOthers;
  if (!toggleAudio && !toggleVideo && !setVolume && !canRemove) {
    return null;
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon-sm"
          variant="secondary"
          aria-label={`Options for ${peer.name}`}
          data-testid="participant_menu_btn"
          className="rounded-full bg-black/50 text-white hover:bg-black/70"
        >
          <Ellipsis />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-60"
        data-testid="participant_menu"
      >
        {!isScreenShare && toggleVideo ? (
          <DropdownMenuItem
            onSelect={toggleVideo}
            data-testid={
              isVideoEnabled
                ? "mute_video_participant_btn"
                : "unmute_video_participant_btn"
            }
          >
            {isVideoEnabled ? <VideoOff /> : <Video />}
            {isVideoEnabled ? "Turn off video" : "Request video"}
          </DropdownMenuItem>
        ) : null}
        {toggleAudio ? (
          <DropdownMenuItem
            onSelect={toggleAudio}
            data-testid={
              isAudioEnabled
                ? "mute_audio_participant_btn"
                : "unmute_audio_participant_btn"
            }
          >
            {isAudioEnabled ? <MicOff /> : <Mic />}
            {isAudioEnabled ? "Mute" : "Request Unmute"}
          </DropdownMenuItem>
        ) : null}
        {setVolume && audioTrack ? (
          <div
            className="flex flex-col gap-2 px-2 py-2"
            data-testid="participant_volume_slider"
            onKeyDown={event => event.stopPropagation()}
          >
            <span className="flex items-center gap-2 text-sm">
              <Volume2 className="size-4 text-muted-foreground" />
              Volume ({volume ?? 100})
            </span>
            <Slider
              min={0}
              max={100}
              step={5}
              value={[volume ?? 100]}
              onValueChange={([value]) => setVolume(value)}
              aria-label={`Volume for ${peer.name}`}
            />
          </div>
        ) : null}
        {canRemove ? <DropdownMenuSeparator /> : null}
        {canRemove && !isScreenShare ? (
          <DropdownMenuItem
            variant="destructive"
            data-testid="remove_participant_btn"
            onSelect={() =>
              hmsActions
                .removePeer(peer.id, "")
                .catch(error =>
                  console.error("could not remove participant", error)
                )
            }
          >
            <UserX />
            Remove Participant
          </DropdownMenuItem>
        ) : null}
        {canRemove && isScreenShare ? (
          <DropdownMenuItem
            variant="destructive"
            data-testid="stop_remote_screenshare_btn"
            onSelect={() => sendEvent({}, { peerId: peer.id })}
          >
            <MonitorX />
            Stop screen share
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
